import { FC, useState } from 'react';

import { router, usePage } from '@inertiajs/react';
import { isEmpty } from 'lodash';

import { DesktopFilters } from './filters/DesktopFilters';
import { DesktopFiltersLayout } from './filters/DesktopFiltersLayout';
import { MobileFilters } from './filters/MobileFilters';
import { ProductsGrid } from './ProductsGrid';
import { FiltersService } from '../../services/FiltersService';
import { ProductType, Expansion, CartonProduct, Universe } from '../../types';

type PageProps = {
    products: CartonProduct[];
    productsCount: number;
    expansions: Expansion[] | null;
    currentProductType: ProductType | null;
    currentExpansion: Expansion | null;
    currentUniverse: Universe;
    next_page: number;
    q: Record<string, string>;
    sort: string;
};

export const Products: FC = () => {
    const {
        products,
        productsCount,
        expansions,
        currentProductType,
        currentExpansion,
        currentUniverse,
        next_page,
        q,
        sort,
    } = usePage<PageProps>().props;
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const fallbackProductTypeSlug =
        !currentProductType && currentUniverse.slug === 'one-piece-tcg'
            ? 'singles'
            : undefined;

    const applySearch = ({
        q,
        expansionSlug,
        preserveState,
    }: {
        q: Record<string, string>;
        expansionSlug?: string;
        preserveState?: boolean;
    }) => {
        const url = FiltersService.generateFilterURL({
            filters: q,
            expansion: expansionSlug,
            productType: currentProductType?.slug ?? fallbackProductTypeSlug,
            universe: currentUniverse.url_path,
            sortBy: sort,
        });

        router.get(
            url,
            {},
            {
                preserveScroll: !expansionSlug,
                preserveState: Boolean(preserveState),
                onStart: () => setLoading(true),
                onSuccess: () => setLoading(false),
            },
        );
    };

    const handleSortChange = (value: string) => {
        const url = FiltersService.generateFilterURL({
            filters: q,
            expansion: currentExpansion?.slug,
            productType: currentProductType?.slug,
            universe: currentUniverse.url_path,
            sortBy: value,
        });

        router.get(
            url,
            {},
            {
                preserveScroll: true,
                onStart: () => setLoading(true),
                onSuccess: () => setLoading(false),
            },
        );
    };

    const resetFilters = () => {
        const url = FiltersService.generateResetFilterUrl({
            universe: currentUniverse.url_path,
            productType: currentProductType?.slug,
        });

        router.get(
            url,
            {},
            {
                preserveScroll: !(currentExpansion || currentProductType),
            },
        );
    };

    const loadMore = () => {
        if (!next_page || loading || loadingMore) return;

        router.get(
            window.location.href,
            { page: next_page },
            {
                preserveScroll: true,
                preserveState: true,
                preserveUrl: true,
                only: ['products', 'next_page'],
                onStart: () => setLoadingMore(true),
                onFinish: () => setLoadingMore(false),
            },
        );
    };

    const areProductsEmpty =
        !products?.length && isEmpty(q) && isEmpty(currentExpansion);

    return (
        <>
            <section className="py-6 sm:py-8">
                <div className="container px-4 sm:px-6 lg:px-8">
                    <DesktopFiltersLayout
                        sidebar={
                            !areProductsEmpty ? (
                                <DesktopFilters
                                    filters={q}
                                    onFilterChange={applySearch}
                                    onResetFilters={resetFilters}
                                    expansion={currentExpansion}
                                    expansions={expansions}
                                    fallbackProductTypeSlug={
                                        fallbackProductTypeSlug
                                    }
                                />
                            ) : null
                        }
                    >
                        <ProductsGrid
                            areProductsEmpty={areProductsEmpty}
                            products={products}
                            nextPage={next_page}
                            loading={loading}
                            loadingMore={loadingMore}
                            loadMore={loadMore}
                            resetFilters={resetFilters}
                            sort={sort}
                            onSortChange={handleSortChange}
                            currentProductType={currentProductType}
                            resultsCount={productsCount}
                            withDesktopSidebar={!areProductsEmpty}
                        />
                    </DesktopFiltersLayout>
                </div>
            </section>

            {!areProductsEmpty && (
                <MobileFilters
                    filters={q}
                    onFilterChange={applySearch}
                    expansion={currentExpansion}
                    fallbackProductTypeSlug={fallbackProductTypeSlug}
                />
            )}
        </>
    );
};
